/**
 * RevenueCustomerHealthPanel (US-17-GR-24)
 *
 * Displays tier distribution, conversion/churn, LTV/CAC and NPS gauge with PRD
 * targets. Data comes from adminMetricsService — no mocked data.
 *
 * Recovered from the deleted user-app panel (frontend commit 9c418bd3f^
 * src/components/agent/admin/RevenueCustomerHealthPanel.tsx) and re-homed into
 * ChellessAdmin conventions.
 *
 * Field names are aligned to the real API DTOs:
 *   RevenueMetricsResponse (NpsModels.cs):
 *     tierDistribution (OBJECT with named tiers, NOT an array)
 *     freeToPaidConversion, monthlyChurn, averageLtv, customerAcquisitionCost
 *     ltvTarget, churnTarget, conversionTarget
 *   NpsMetricsResponse (NpsModels.cs):
 *     score, totalResponses, promoters, passives, detractors, scoreTarget
 *
 * Failure/staleness honesty (US-17-GR-34, AC-2 / AC-4): the API swallows
 * exceptions in these two computations and returns 200 with a zero-valued
 * payload, so a broken query would otherwise render as "NPS = 0, churn = 0%"
 * — visually identical to a genuine measurement of zero. Both responses now
 * carry `dataUnavailable` (true ⇒ every numeric field is meaningless) and
 * `asOfUtc` (snapshot age). When `dataUnavailable` is true this panel renders
 * a dashed-border "unavailable" note instead of the KPI grid/NPS bar — the
 * same idiom ActivationFunnelPage.tsx uses for its realized-stage
 * degradation — and never falls through to the numeric branch. When the
 * snapshot is older than 36h (nightly aggregator cadence + slack) a Stale
 * badge shows the age next to the section header.
 */

import { useEffect, useState, useCallback, startTransition, type ReactNode } from 'react';
import { DollarSign, RefreshCw, AlertCircle, AlertTriangle, Clock, Star } from 'lucide-react';
import type { RevenueMetrics, NpsMetrics, CacDisplay } from '../services/adminMetricsService';
import { getRevenueMetrics, getNpsMetrics, resolveCacDisplay } from '../services/adminMetricsService';
import { AbbrLabel } from './AbbrLabel';

/** Snapshot age past which data is considered stale — nightly aggregator (~24h) plus slack. */
const STALE_THRESHOLD_HOURS = 36;

function currency(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

function hoursSince(iso: string): number {
  return (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60);
}

function formatAge(hours: number): string {
  if (hours < 24) return `${Math.round(hours)}h ago`;
  const days = Math.floor(hours / 24);
  const remHours = Math.round(hours % 24);
  return `${days}d ${remHours}h ago`;
}

/**
 * Age badge shown only when `asOfUtc` is older than STALE_THRESHOLD_HOURS —
 * the reader must be able to tell a fresh figure from a stale one at a
 * glance, not just from a hover tooltip (AC-4).
 */
function StaleBadge({ asOfUtc }: { asOfUtc: string | null }) {
  if (!asOfUtc) return null;
  const hours = hoursSince(asOfUtc);
  if (hours <= STALE_THRESHOLD_HOURS) return null;
  const age = formatAge(hours);
  return (
    <span
      title={`Last refreshed ${age} — older than the ~24h aggregator cadence plus slack.`}
      aria-label={`Stale data: last refreshed ${age}`}
      tabIndex={0}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-[10px] font-medium text-amber-300 cursor-help"
    >
      <Clock className="w-3 h-3" aria-hidden="true" />
      Stale &middot; {age}
    </span>
  );
}

/**
 * Distinct "figures unavailable" note (AC-2). Reuses the ActivationFunnelPage
 * idiom: dashed border, amber AlertTriangle, explanatory copy — deliberately
 * NOT a numeric tile, so it can never be mistaken for a measured zero.
 */
function MetricsUnavailableNote({ label, explanation }: { label: ReactNode; explanation: string }) {
  return (
    <div className="p-4 bg-zinc-800/30 rounded-lg border border-dashed border-zinc-700" role="status">
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" aria-hidden="true" />
        <h4 className="text-sm font-medium text-zinc-300">{label}</h4>
      </div>
      <p className="text-xs text-zinc-500">{explanation}</p>
    </div>
  );
}

function KpiCard({ label, value, sub }: { label: ReactNode; value: string; sub?: string }) {
  return (
    <div className="bg-zinc-800/50 rounded-lg p-4">
      <p className="text-2xl font-semibold text-zinc-100">{value}</p>
      <p className="text-xs text-zinc-400 mt-1">{label}</p>
      {sub && <p className="text-xs text-zinc-600 mt-0.5">{sub}</p>}
    </div>
  );
}

/**
 * Work item 17471. The CAC tile, which must never show a bare number.
 *
 * DEC-2026-07-23 ratifies CAC at $40-60 per activated signup, paid-ads-only, and attaches a
 * standing rule: signup-CPA and activated-CAC are distinct units and are never blended; any CAC
 * figure carries an as-of date and a named source; the anchor is stamped an unvalidated
 * estimate. The tile used to render a configured 30 with a "target $30" subtitle — outside the
 * band on both halves, derived from no spend, undated, unsourced and unstamped.
 *
 * So there are two shapes here and no third. Either the figure is fully provenanced and in
 * band, in which case it renders with its unit, its date, its source and the stamp; or it
 * renders no number at all and says why. A blank tile makes an operator ask a question; a
 * confident wrong tile makes them act.
 */
function CacCard({ display }: { display: CacDisplay }) {
  if (display.kind === 'unavailable') {
    return (
      <div
        className="bg-zinc-800/50 rounded-lg p-4 border border-dashed border-zinc-700"
        role="status"
      >
        <p className="text-sm font-medium text-zinc-400 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" aria-hidden="true" />
          Not available
        </p>
        <p className="text-xs text-zinc-400 mt-1">
          <AbbrLabel term="CAC" />
        </p>
        <p className="text-xs text-zinc-500 mt-0.5">{display.reason}</p>
      </div>
    );
  }

  const { anchor, valueUsd } = display;
  const asOf = anchor.asOfUtc ? new Date(anchor.asOfUtc).toISOString().slice(0, 10) : null;

  return (
    <div className="bg-zinc-800/50 rounded-lg p-4">
      <p className="text-2xl font-semibold text-zinc-100">{currency(valueUsd)}</p>
      <p className="text-xs text-zinc-400 mt-1">
        <AbbrLabel term="CAC" /> &middot; {anchor.unit}
      </p>
      <p className="text-xs text-zinc-600 mt-0.5">
        ratified band {currency(anchor.ratifiedBandLowUsd)}&ndash;{currency(anchor.ratifiedBandHighUsd)}
      </p>
      <p className="text-xs text-zinc-600 mt-0.5">
        {anchor.isUnvalidatedEstimate && <>unvalidated estimate &middot; </>}
        {asOf && <>as of {asOf} &middot; </>}
        {anchor.source}
      </p>
    </div>
  );
}

/** Simple horizontal bar representing NPS breakdown (promoters / passives / detractors).
 * promoters, passives, detractors are percentages (0-100) from NpsMetricsResponse. */
function NpsBar({ promoters, passives, detractors }: { promoters: number; passives: number; detractors: number }) {
  const total = promoters + passives + detractors;
  if (total === 0) return null;
  return (
    <div className="flex h-3 rounded-full overflow-hidden gap-px" aria-label="NPS distribution bar">
      <div style={{ width: `${promoters}%` }} className="bg-emerald-500" title={`Promoters ${promoters.toFixed(1)}%`} />
      <div style={{ width: `${passives}%` }} className="bg-amber-400" title={`Passives ${passives.toFixed(1)}%`} />
      <div style={{ width: `${detractors}%` }} className="bg-red-500" title={`Detractors ${detractors.toFixed(1)}%`} />
    </div>
  );
}

/**
 * Converts the TierDistribution object into a display array of real subscription
 * tiers. `unknown` is deliberately absent — it is the never-subscribed bucket, not a
 * tier, and is surfaced separately below the bars.
 *
 * `Pro` was missing from this list, so a Pro subscriber was dropped on the floor even
 * though the API sends the value. DEC-2026-07-30 made Pro a live tier.
 */
function tierEntries(td: RevenueMetrics['tierDistribution']): { label: string; pct: number }[] {
  return [
    { label: 'Basic', pct: td.basic },
    { label: 'Pro', pct: td.pro },
    { label: 'Premium', pct: td.premium },
    { label: 'Entrepreneur', pct: td.entrepreneur },
    { label: 'Growth', pct: td.growth },
    { label: 'Elite', pct: td.elite },
  ].filter((t) => t.pct > 0);
}

export function RevenueCustomerHealthPanel() {
  const [revenue, setRevenue] = useState<RevenueMetrics | null>(null);
  const [nps, setNps] = useState<NpsMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, n] = await Promise.all([getRevenueMetrics(), getNpsMetrics()]);
      setRevenue(r);
      setNps(n);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    startTransition(() => { void load(); });
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <RefreshCw className="w-5 h-5 text-zinc-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
        <div>
          <p className="text-sm text-red-400 font-medium">Failed to load revenue metrics</p>
          <p className="text-xs text-zinc-500 mt-0.5">{error}</p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="ml-auto px-3 py-1.5 text-xs rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Revenue KPIs */}
      {revenue && (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <DollarSign className="w-4 h-4 text-emerald-400" />
            <h3 className="text-sm font-medium text-zinc-200">Revenue &amp; Customer Health</h3>
            {!revenue.dataUnavailable && <StaleBadge asOfUtc={revenue.asOfUtc} />}
          </div>

          {revenue.dataUnavailable ? (
            <MetricsUnavailableNote
              label="Revenue figures unavailable"
              explanation="The revenue computation failed on the server. LTV, CAC, churn, conversion and tier distribution are not a measurement of zero — they are not shown. Retry shortly or check server logs."
            />
          ) : (
            <>
              {/* Key monetary health metrics the API actually provides */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <KpiCard
                  label={<>Avg <AbbrLabel term="LTV" /></>}
                  value={currency(revenue.averageLtv)}
                  sub={`target ${currency(revenue.ltvTarget)}`}
                />
                <CacCard display={resolveCacDisplay(revenue.customerAcquisitionCost)} />
                <KpiCard
                  label="Monthly Churn"
                  value={`${revenue.monthlyChurn.toFixed(1)}%`}
                  sub={`target ≤${revenue.churnTarget.toFixed(1)}%`}
                />
                <KpiCard
                  label="Free → Paid"
                  value={`${revenue.freeToPaidConversion.toFixed(1)}%`}
                  sub={`target ${revenue.conversionTarget.toFixed(1)}%`}
                />
              </div>

              {/* Tier distribution (object with named properties) */}
              {(() => {
                const tiers = tierEntries(revenue.tierDistribution);
                const neverSubscribedPct = revenue.tierDistribution.unknown;
                // Rendering nothing when every profile is unsubscribed reads as "no data
                // yet". It is data: it says nobody has subscribed. Say that instead.
                if (tiers.length === 0 && neverSubscribedPct <= 0) return null;
                return (
                  <div>
                    <p className="text-xs text-zinc-400 mb-2">Tier Distribution</p>
                    {tiers.length > 0 ? (
                      <div className="space-y-2">
                        {tiers.map((tier) => (
                          <div key={tier.label} className="flex items-center gap-3">
                            <span className="w-24 text-xs text-zinc-300 flex-shrink-0">{tier.label}</span>
                            <div className="flex-1 h-2 bg-zinc-700 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-violet-500 rounded-full"
                                style={{ width: `${tier.pct}%` }}
                              />
                            </div>
                            <span className="text-xs text-zinc-500 w-14 text-right">
                              {tier.pct.toFixed(1)}%
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-zinc-500">No profile holds a subscription of any tier.</p>
                    )}
                    {neverSubscribedPct > 0 && (
                      <p className="text-[10px] text-zinc-600 mt-2">
                        {neverSubscribedPct.toFixed(1)}% of profiles have no subscription on record — not a
                        tier, and not counted above.
                      </p>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </>
      )}

      {/* NPS */}
      {nps && (
        <div className="pt-2 border-t border-zinc-800">
          {nps.dataUnavailable ? (
            <>
              <div className="flex items-center gap-2 mb-3">
                <Star className="w-4 h-4 text-amber-400" />
                <h4 className="text-sm font-medium text-zinc-200">
                  <AbbrLabel term="NPS" /> Score
                </h4>
              </div>
              {nps.dataUnavailableReason === 'noResponses' ? (
                <MetricsUnavailableNote
                  label="No NPS responses yet"
                  explanation="No one has answered the NPS survey in the last 30 days, so there is no score to show — this is not a score of zero."
                />
              ) : (
                <MetricsUnavailableNote
                  label="NPS figures unavailable"
                  explanation="The NPS computation failed on the server. Score, response count, and the promoter/passive/detractor breakdown are not a measurement of zero — they are not shown. Retry shortly or check server logs."
                />
              )}
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Star className="w-4 h-4 text-amber-400" />
                <h4 className="text-sm font-medium text-zinc-200">
                  <AbbrLabel term="NPS" /> Score:{' '}
                  <span
                    className={`font-bold ${nps.score >= nps.scoreTarget ? 'text-emerald-400' : nps.score >= 0 ? 'text-amber-400' : 'text-red-400'}`}
                  >
                    {nps.score}
                  </span>
                  <span className="text-xs text-zinc-500 font-normal ml-1">(target {nps.scoreTarget})</span>
                </h4>
                <StaleBadge asOfUtc={nps.asOfUtc} />
                <span className="ml-auto text-xs text-zinc-500">
                  {nps.totalResponses.toLocaleString()} responses
                </span>
              </div>
              <NpsBar
                promoters={nps.promoters}
                passives={nps.passives}
                detractors={nps.detractors}
              />
              <div className="flex justify-between text-xs mt-1.5">
                <span className="text-emerald-400">Promoters {nps.promoters.toFixed(1)}%</span>
                <span className="text-amber-400">Passives {nps.passives.toFixed(1)}%</span>
                <span className="text-red-400">Detractors {nps.detractors.toFixed(1)}%</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default RevenueCustomerHealthPanel;
